/**
 * Threads plus the streaming turn for the active one, for any agent scope
 * (case, client or practice). The Case IDE wraps this as `useCaseAgentChat`
 * and supplies adapters (editor selection, artifact opening, invalidation);
 * the agent panel uses it directly.
 *
 * The thread is a team channel: people-only mentions post a team message,
 * `@Agent` (or nobody) starts an agent turn; every outgoing message carries
 * the author stamp; `active_turn` is the lock held by whoever is streaming.
 */
import { useChat, type UseChatHelpers } from "@ai-sdk/react";
import * as Sentry from "@sentry/react";
import { DefaultChatTransport, type ToolUIPart, type UIMessage } from "ai";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";

import { getAuthToken } from "../../api/axiosConfig";
import { noteAgentTurnSent } from "../agentAttention/alertPrefs";
import { keepActiveTab } from "../appTabs/context/appTabsStore";
import { parseMentions } from "../caseIde/chat/mentions";
import type {
  AgentMode,
  AskUserAnswers,
  CaseIdeDataParts,
  ClientToolRequestData,
  InvalidateTarget,
  OpenArtifactData
} from "../caseIde/types";
import type { ConnectAppsOutput } from "../connectors/types";
import { notifyCompaniesInvalidated } from "../companies/queries";
import {
  notifyIntakeTemplateOpened,
  notifyIntakeTemplatesInvalidated
} from "../firmKnowledge/intakeTemplateEvents";
import { notifyTimeInvalidated } from "../timeTracking/timeEvents";
import { notifySettingsInvalidated } from "../onboarding/settingsEvents";
import type { SuggestedAgentsOutput } from "../onboarding/types";
import {
  AgentAPI,
  agentChatUrl,
  agentStreamUrl,
  type ClientToolOutcomeData,
  isNoTurnRunning,
  type ActiveTurn,
  type AgentThread,
  type BuildStamp,
  type FailedInfo,
  type QueuedMessage,
  type SteerItem,
  type StoppedInfo,
  type StopRequested,
  type ThreadComposerUpdate,
  type ThreadMetaUpdate
} from "./api/agentApi";
import type { AttachmentFilePart } from "./attachments/types";
import { messageParts } from "./attachments/utils";
import type { OnScreen, RecentOnScreen } from "./onScreen/onScreen";
import type { EntityKind } from "./refs/entityRefs";
import { runningTurnsStore } from "./runningTurnsStore";
import {
  AGENT_FOR_SCOPE,
  ASSISTANT_PERSONA,
  AUTO_MODEL_ID,
  normalizeAgentKind,
  type AgentChange,
  type AgentKind,
  type FollowUpsData,
  type AgentScope,
  type AnyAgentKind,
  type ApprovalDecision,
  type ApprovalReview,
  type HandoffResult,
  type ModelUsed,
  type OptimizeFor,
  type ScopeKind
} from "./types";

export type MessageAuthor = {
  uid: string;
  name: string;
  email?: string | null;
  avatar_url?: string | null;
  /**
   * `"agent"` for a handoff brief another scope's agent posted, or for the
   * reply of the agent member that answered in a channel. `"system"` for a
   * notification the backend posted when background work the agent started
   * (draft, organizer, PDF edit, packet, export) finished; rendered as an
   * event line, never as a person's bubble.
   */
  kind?: "user" | "agent" | "system";
  /** Messages stored before the merge still say `case_assistant`/`client_manager`. */
  agent_kind?: AnyAgentKind | null;
  /** The scope the agent works in, when it is a channel member. */
  scope_kind?: ScopeKind | null;
  scope_id?: string | null;
};

export type MentionRef = {
  kind: "user" | "agent";
  /** A uid, `agent`, or `<scope_kind>:<scope_id>` for a channel member. */
  id: string;
  label: string;
};

/** Which agent member a message was for. */
export type ResponderRef = { scope_kind: ScopeKind | string; scope_id: string };

/**
 * Per-message metadata. Every user message carries who wrote it and whom they
 * named (the thread is a team channel); `audience` says whether it was for an
 * agent or team chat and `responder` which agent. A message that starts a
 * plan build carries the plan (and its todos as they stood), so the
 * transcript shows a Build card in place of the prompt.
 */
export type AgentMessageMetadata = {
  build?: BuildStamp;
  author?: MessageAuthor;
  mentions?: MentionRef[];
  audience?: "agent" | "team";
  responder?: ResponderRef | null;
  /**
   * On a system notice: the background run it reports on, keyed like the
   * `run: {kind, id}` the start tool returned, so the run's card can resolve
   * from the notice before the tree or poll catches up.
   */
  event?: RunEvent | null;
  /** On an assistant message: the turn ended early (an explicit stop). */
  stopped?: StoppedInfo | null;
  /** On an assistant message: the model that ran the turn. */
  model?: ModelUsed | null;
  /**
   * On an assistant message persisted mid-turn at a server hand-off: the
   * reply so far, continued by the resumed run (`resumed: false` once nobody
   * will). Reattaching replaces it with the full replay.
   */
  interrupted?: { reason?: string; at?: string; resumed?: boolean } | null;
  /**
   * On an assistant message stored mid-turn: the reply as of its last
   * finished step. Reattaching replaces it with the full replay.
   */
  checkpoint?: boolean;
  /** The reply could not be stored in full; its text and open cards were kept. */
  degraded?: boolean;
  /** A card the transcript lost, put back from the thread's pause. */
  restored?: boolean;
  /** On a user message sent by clicking an offer under the agent's reply. */
  accepted_offer?: { id: string; choice: string } | null;
  /** On an assistant message: what was done with each support card, by tool call id. */
  support_cards?: Record<string, SupportCardRecord> | null;
  /** On the system note that seeds a Continue in Case thread. */
  continued_from?: { thread_id: string; tool_call_id: string } | null;
  /** On a user message: the record open on the page that the turn used. */
  context?: TurnContextRef | null;
  /** On a user message: the library sources the attorney picked to research. */
  research_sources?: string[];
  /** On a Case Prep workflow's prompt: which workflow and prompt version sent it. */
  workflow?: {
    id: string;
    template: string;
    version: number;
    /** Part of Run Everything: the server keeps the step from asking mid-run. */
    chained?: boolean;
  };
};

/** The open record the server checked for a turn; `route` is the page it was on. */
export type TurnContextRef = {
  kind: EntityKind;
  id: string;
  label: string | null;
  route?: string | null;
};

export type SupportCardState = "opened" | "dismissed";

export type SupportCardRecord = {
  state: SupportCardState;
  by?: string;
  at_ns?: number;
};

/** A background run's terminal status as reported by a system notice. */
export type RunEvent = {
  kind: string;
  id: string;
  status: string;
};

/** True for a backend-posted notification about background work. */
export const isSystemNotice = (metadata?: AgentMessageMetadata | null) =>
  metadata?.author?.kind === "system";

/** Prefix of the turn-lock uid a backend-initiated turn holds. */
export const SYSTEM_TURN_UID_PREFIX = "system:";

/**
 * A turn lock the server shut down under: handed off (`interrupted_at`) or
 * already taken over by the worker pool (`resumed`). The reply lands over the
 * realtime channel under the same message id; meanwhile the transcript shows
 * a spinner instead of an error, whoever's turn it is.
 */
export const isContinuingTurn = (
  turn:
    | { interrupted_at?: string | null; resumed?: boolean | null }
    | null
    | undefined
): boolean => !!turn && (!!turn.resumed || !!turn.interrupted_at);

/**
 * Copy for someone else's turn lock (no trailing ellipsis; callers add their
 * own). A system holder is the assistant reporting on background work it was
 * woken for, not a person talking to it.
 */
export const activeTurnCopy = (
  turn: {
    uid?: string | null;
    name?: string | null;
    interrupted_at?: string | null;
    resumed?: boolean | null;
  },
  agentLabel: string,
  /** The viewer: their own turn running elsewhere (another tab, a reload) reads differently. */
  meUid?: string | null
): string =>
  isContinuingTurn(turn)
    ? `${agentLabel} is continuing after a server restart`
    : (turn.uid ?? "").startsWith(SYSTEM_TURN_UID_PREFIX)
    ? `${turn.name || "Casey"} is reporting on background work`
    : meUid && turn.uid === meUid
    ? `${agentLabel} is working on your message`
    : `${turn.name || "Someone"} is talking to ${agentLabel}`;

/** `data-*` parts every agent may stream; the IDE ones are no-ops outside it. */
export type AgentDataParts = CaseIdeDataParts & {
  /** `handoff_to_agent` ran: work now lives in another scope's thread. */
  handoff: HandoffResult;
  /** A "send now" message the model received mid-turn (kept in history). */
  steer: SteerItem;
  /** The turn ended early (kept in history). */
  stopped: StoppedInfo;
  /** The turn ended in an error (kept in history). */
  failed: FailedInfo;
  /** Which model runs this turn (kept in history). */
  model: ModelUsed;
  /**
   * What an approval card says beyond the raw args (kept in history, keyed by
   * approval id): the target, the recipient, whether it could be undone, and
   * which answers apply.
   */
  "approval-review": ApprovalReview;
  /** A recorded, undoable change (kept in history, keyed by change id). */
  change: AgentChange;
  /** The reply left case-integrity items for later (kept in history). */
  "follow-ups": FollowUpsData;
  /**
   * An offer added under the reply after the turn ended (save what the
   * conversation taught as a skill); parsed by `offerOf`.
   */
  offer: unknown;
  /** Transient: the turn ended cleanly and this queued message is next. */
  "queue-next": { id: string; uid: string; name: string };
  /**
   * Transient: the open record the server checked for this turn, for the
   * sent message's context line (stored on it as `metadata.context`).
   */
  "turn-context": TurnContextRef & { user_message_id?: string | null };
  /**
   * Live-only (streamed into the message, never in history): what the agent
   * is doing right now ("Reading the case"). Shown while it is the last part.
   */
  status: TurnStatusData;
  /**
   * Transient: a delegated agent's tool call or result, under the call that
   * delegated (a `task` subagent, or the drafting / research / organizer run
   * a `generate_draft` / `start_research` / `start_autocategorize` waits on).
   */
  step: StepData;
};

/** `data-status`: a short present-tense label, optionally bound to a tool call. */
export type TurnStatusData = { label: string; toolCallId?: string | null };

/** `data-step`: one nested tool call inside a delegated agent's run. */
export type StepData = {
  /** The delegating tool call (`task`, `generate_draft`, ...) the step sits under. */
  taskCallId: string;
  callId: string;
  name?: string;
  input?: unknown;
  state: "input-available" | "output-available" | "output-error";
  output?: unknown;
  error?: string;
};

/** Live nested steps, keyed by the delegating call they belong to. */
export type StepsByTask = Record<string, StepData[]>;

const EMPTY_STEPS: StepsByTask = {};

/**
 * How often `useChat` re-renders while a stream is in flight. Tokens arrive
 * far faster than 25fps is worth; Cursor's transcript paints at about this rate.
 */
export const STREAM_THROTTLE_MS = 40;

/** How long before the same running turn may be reattached to. */
export const REATTACH_SAME_TURN_MS = 15_000;

/**
 * After a stream that ended with its finish part, how long the thread's lock
 * may still name that turn (the server releases it just after) without it
 * being taken for a turn to attach to.
 */
export const CLEAN_FINISH_GRACE_MS = 5_000;

/**
 * A stream that ended without the server's finish part and without the
 * user stopping it was cut somewhere between the turn and the browser
 * (proxy, load balancer, network); the turn is likely still running.
 */
export const streamWasCut = (end: {
  isAbort: boolean;
  isDisconnect: boolean;
  isError: boolean;
  finishReason?: string;
}): boolean =>
  !end.isAbort && (end.isDisconnect || (!end.isError && !end.finishReason));

const normalized = (text: string) =>
  text.replace(/\s+/g, " ").trim().toLowerCase();

/** The text of a message's text parts. */
const textOfMessage = (message: {
  parts?: ReadonlyArray<{ type: string; text?: string }>;
}): string =>
  (message.parts ?? [])
    .filter((p) => p.type === "text")
    .map((p) => p.text ?? "")
    .join("\n");

/** The last user message's text, normalized for comparison. */
export const lastUserText = (
  messages: ReadonlyArray<{
    role: string;
    parts?: ReadonlyArray<{ type: string; text?: string }>;
  }>
): string | null => {
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    if (messages[i].role === "user")
      return normalized(textOfMessage(messages[i]));
  }
  return null;
};

/**
 * A queued message the transcript already holds: its own id is there (it
 * ran), or it repeats the message that just ran (sent again after the stream
 * looked stuck).
 */
export const alreadyInTranscript = (
  item: Pick<QueuedMessage, "text" | "message_id" | "attachments">,
  messages: ReadonlyArray<{
    id: string;
    role: string;
    parts?: ReadonlyArray<{ type: string; text?: string }>;
  }>
): boolean => {
  if (item.message_id && messages.some((m) => m.id === item.message_id))
    return true;
  if (item.attachments?.length) return false;
  const last = lastUserText(messages);
  return last !== null && last !== "" && last === normalized(item.text);
};

/** The last assistant message, without copying the array. */
export const lastAssistantMessage = <M extends { role: string }>(
  messages: readonly M[]
): M | null => {
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    if (messages[i].role === "assistant") return messages[i];
  }
  return null;
};

export type AgentUIMessage = UIMessage<AgentMessageMetadata, AgentDataParts>;

/** The signed-in user, for the author stamp on outgoing messages. */
export type Me = {
  uid: string;
  name: string;
  email?: string | null;
  avatarUrl?: string | null;
};

/** The case or client on screen as a turn is sent. */
export type TurnFocus = {
  kind: "case" | "client";
  id: string;
  /** The intake open in the Requests workspace, under a client focus. */
  intake_id?: string | null;
};

/** What the transport sends as `selection` (the Case IDE's editor context). */
export type SerializedSelection = {
  artifact: {
    kind: string;
    id: string;
    title?: string;
    page?: number | null;
  } | null;
  text: string | null;
  selected_node_ids?: string[];
  /** The page the user is on (the time review card reads its query). */
  route?: string | null;
  /**
   * What the user is looking at: the default target and the reference for
   * off-focus approvals. The server checks it against the firm.
   */
  focus?: TurnFocus | null;
  /** The draft open in a live editor, when there is one (see `describeOpenDraft`). */
  open_draft?: Record<string, unknown> | null;
  /**
   * Case files open in an in-app office editor (see `describeOpenFiles`), so
   * the server can address spreadsheet / slides tools to the live editor.
   */
  open_files?: Record<string, unknown>[];
  /** The intake open in the Requests IDE, so "this intake" resolves server-side. */
  open_intake?: {
    id: string;
    title?: string | null;
    status?: string | null;
    selected_node_ids?: string[];
  } | null;
  /** The submission under review in the Requests IDE. */
  open_submission?: {
    intake_id: string;
    submission_id?: string | null;
    selected_answer_key?: string | null;
  } | null;
  /**
   * The record the page has open (a template, company, library entry, time
   * entry…), so "this" resolves server-side. Checked against the firm there.
   */
  open?: OnScreen | null;
  /** Records viewed recently, newest first, for "the one I was just in". */
  recent?: RecentOnScreen[];
};

/** Host hooks the chat calls into; all optional so the panel can omit them. */
export type ChatAdapters = {
  /** Editor context to attach to each turn. */
  selection?: () => SerializedSelection | null;
  /** `data-invalidate`: the agent changed case data the host caches. */
  onInvalidate?: (targets: InvalidateTarget[]) => void;
  /** `data-open-artifact`: show a file/draft/exhibit. */
  onOpenArtifact?: (artifact: OpenArtifactData) => void;
  /** `data-handoff`: another agent's thread now holds the work. */
  onHandoff?: (handoff: HandoffResult) => void;
  /**
   * `data-client-tool`: the turn asks this browser to act on the draft open in
   * its editor. The outcome is posted back for the waiting tool.
   */
  onClientTool?: (
    request: ClientToolRequestData
  ) => Promise<ClientToolOutcomeData>;
  /** Resolve a title for an artifact the agent asks to open. */
  titleFor?: (artifact: OpenArtifactData) => string;
};

/**
 * `{"error": "...", "active_turn": {...}, "queued": {...} | null}` from a 409,
 * as the transport surfaces it. `queued` is set when the server held the
 * message for after the running turn.
 */
export const parseTurnInProgress = (
  message: string
): {
  error: string;
  activeTurn: ActiveTurn | null;
  queued: QueuedMessage | null;
} | null => {
  try {
    const parsed = JSON.parse(message) as {
      error?: unknown;
      active_turn?: ActiveTurn | null;
      queued?: QueuedMessage | null;
    };
    if (!parsed || typeof parsed !== "object" || !("active_turn" in parsed))
      return null;
    return {
      error:
        typeof parsed.error === "string"
          ? parsed.error
          : "Someone is talking to the agent right now",
      activeTurn: parsed.active_turn ?? null,
      queued: parsed.queued ?? null
    };
  } catch {
    return null;
  }
};

/** The `code` of the error part a turn ends with when the server shut down under it. */
export const SERVER_RESTART_CODE = "server_restart";

/**
 * `{"code": "server_restart", "error": "...", "resuming": bool, "message_id"}`
 * as the `error` part of a stream the server ended because its process was
 * going away. With `resuming` the worker pool continues the turn from its
 * checkpoint and the finished message arrives over the realtime channel under
 * `message_id` (the partial one already in the transcript); without it the
 * turn is over and the person is asked to send again.
 */
export const parseServerRestart = (
  message: string
): { error: string; resuming: boolean; messageId: string | null } | null => {
  try {
    const parsed = JSON.parse(message) as {
      code?: unknown;
      error?: unknown;
      resuming?: unknown;
      message_id?: unknown;
    };
    if (
      !parsed ||
      typeof parsed !== "object" ||
      parsed.code !== SERVER_RESTART_CODE
    )
      return null;
    return {
      error:
        typeof parsed.error === "string"
          ? parsed.error
          : "The server restarted before this reply finished.",
      resuming: parsed.resuming === true,
      messageId:
        typeof parsed.message_id === "string" ? parsed.message_id : null
    };
  } catch {
    return null;
  }
};

/**
 * The AI SDK could not match a streamed tool part to a call in the message it
 * holds (a resumed stream after the client's copy moved on). Not the
 * person's problem: the persisted transcript is reloaded instead.
 */
export const isToolCallMismatch = (message: string): boolean =>
  /no tool invocation found|tool call id .* not found|no tool call found/i.test(
    message
  );

/** `{"error": "queue_out_of_order"}`: a stale dispatch of a queued message. */
export const isQueueOutOfOrder = (message: string): boolean => {
  try {
    const parsed = JSON.parse(message) as { error?: unknown };
    return parsed?.error === "queue_out_of_order";
  } catch {
    return false;
  }
};

export const ASK_USER_PART = "tool-ask_user";
export const CONNECT_APPS_PART = "tool-connect_apps";
export const SUGGEST_AGENTS_PART = "tool-show_suggested_agents";
/**
 * Tool parts the *client* resolves by writing their output (a questionnaire
 * answered, apps connected on the connect card). The server pauses the graph
 * on them and resumes with the output on the next request; mirrors
 * `CLIENT_RESOLVED_TOOLS` in `streaming.py`.
 */
export const CLIENT_RESOLVED_PARTS: ReadonlySet<string> = new Set([
  ASK_USER_PART,
  CONNECT_APPS_PART,
  SUGGEST_AGENTS_PART
]);

type PausePart = { type: string; state?: string; toolCallId?: string };

const stateOf = (p: { type: string }): string | undefined =>
  "state" in p ? (p as PausePart).state : undefined;

/** An approval card or a client-resolved card (answered or not). */
const isPausePart = (p: { type: string }): boolean => {
  const state = stateOf(p);
  if (state === "approval-requested" || state === "approval-responded")
    return true;
  return (
    CLIENT_RESOLVED_PARTS.has(p.type) &&
    (state === "input-available" || state === "output-available")
  );
};

/** A pause card still waiting on the person. */
export const isOpenPause = (p: { type: string }): boolean => {
  const state = stateOf(p);
  return (
    state === "approval-requested" ||
    (CLIENT_RESOLVED_PARTS.has(p.type) && state === "input-available")
  );
};

const isContentPart = (p: { type: string }): boolean =>
  p.type === "text" ||
  p.type === "reasoning" ||
  p.type === "dynamic-tool" ||
  p.type.startsWith("tool-");

/**
 * The pause the message is on now: the trailing pause cards of its last step
 * (the server emits nothing but data parts after pausing). Walking back from
 * the end, prose or another tool's card ends the pause, and so does a second
 * client-resolved card (the server pauses on one at a time). Cards before
 * that were left behind by a run that was lost or replaced; nothing will
 * resolve them, so they must not hold the turn.
 */
export const latestPause = <P extends { type: string }>(
  parts: readonly P[]
): P[] => {
  const types = parts.map((p) => p.type);
  const step = parts.slice(types.lastIndexOf("step-start") + 1);
  const pause: P[] = [];
  let clientResolved = false;
  for (let i = step.length - 1; i >= 0; i -= 1) {
    const part = step[i];
    if (isPausePart(part)) {
      const resolvedByClient = CLIENT_RESOLVED_PARTS.has(part.type);
      if (resolvedByClient && clientResolved) break;
      clientResolved ||= resolvedByClient;
      pause.unshift(part);
    } else if (isContentPart(part)) {
      break;
    }
  }
  return pause;
};

/**
 * Pause cards in `parts` still waiting on the person that are not part of the
 * latest pause: rendered as no longer needed rather than as live prompts.
 */
export const stalePauseIds = (
  parts: readonly { type: string }[]
): ReadonlySet<string> => {
  const live = new Set(latestPause(parts));
  const out = new Set<string>();
  parts.forEach((p) => {
    const id = (p as PausePart).toolCallId;
    if (id && isOpenPause(p) && !live.has(p)) out.add(id);
  });
  return out;
};

export const STALE_PAUSE_TEXT =
  "No longer needed: the agent moved on without this.";

/** `parts` with stale pause cards shown as closed (display only). */
export const closeStalePauses = <P extends { type: string }>(
  parts: readonly P[]
): readonly P[] => {
  const stale = stalePauseIds(parts);
  if (!stale.size) return parts;
  return parts.map((p) =>
    stale.has((p as PausePart).toolCallId ?? "")
      ? ({ ...p, state: "output-error", errorText: STALE_PAUSE_TEXT } as P)
      : p
  );
};

const lastAssistantPause = (messages: UIMessage[]): UIMessage["parts"] => {
  const last = messages[messages.length - 1];
  if (!last || last.role !== "assistant") return [];
  return latestPause(last.parts);
};

/**
 * Resend when the attorney decided every approval of the latest pause. The
 * SDK's `lastAssistantMessageIsCompleteWithApprovalResponses` weighs every
 * tool card of the last step, so a card left open by a lost run blocked it.
 */
export const lastAssistantMessageIsCompleteWithDecisions = ({
  messages
}: {
  messages: UIMessage[];
}): boolean => {
  const pause = lastAssistantPause(messages);
  return (
    pause.some((p) => stateOf(p) === "approval-responded") &&
    !pause.some(isOpenPause)
  );
};

/**
 * Resend when the attorney resolved a client-resolved pause: the latest pause
 * holds an `ask_user` / `connect_apps` part whose output the client wrote
 * (`addToolOutput`). After the server resumes, that part is no longer in the
 * last step (a new step started), so this cannot loop.
 */
export const lastAssistantMessageIsCompleteWithAnswers = ({
  messages
}: {
  messages: UIMessage[];
}): boolean => {
  const pause = lastAssistantPause(messages);
  const asks = pause.filter((p) => CLIENT_RESOLVED_PARTS.has(p.type));
  if (!asks.length) return false;
  return !pause.some(isOpenPause);
};

export const sendAutomaticallyWhen = (args: { messages: UIMessage[] }) =>
  lastAssistantMessageIsCompleteWithDecisions(args) ||
  lastAssistantMessageIsCompleteWithAnswers(args);

/**
 * The transcript's last message is waiting on the person: an approval card
 * without a decision, an `ask_user` questionnaire without answers, or a
 * connect card whose apps are not connected yet. Queued messages are not
 * dispatched over a prompt (resolving it resumes the turn).
 */
export const transcriptWaiting = (messages: UIMessage[]): boolean =>
  lastAssistantPause(messages).some(isOpenPause);

/**
 * The composer's placeholder while a question is docked on it: a message sent
 * now is the reply, and the server skips the card's questions.
 */
export const PENDING_QUESTION_PLACEHOLDER =
  "Or reply in your own words (skips the questions)";

/**
 * The `ask_user` part the attorney still has to answer, when the transcript's
 * last message is waiting on one. Same predicate as `transcriptWaiting`, so
 * the docked question card and the queue gate agree.
 */
export const pendingAskUserPart = (
  messages: UIMessage[]
): ToolUIPart | null => {
  const part = lastAssistantPause(messages).find(
    (p) => p.type === ASK_USER_PART && isOpenPause(p)
  );
  return part ? (part as ToolUIPart) : null;
};

/** The approval card the transcript's last message is waiting on, if any. */
export const pendingApprovalPart = (
  messages: UIMessage[]
): ToolUIPart | null => {
  const part = lastAssistantPause(messages).find(
    (p) => stateOf(p) === "approval-requested"
  );
  return part ? (part as ToolUIPart) : null;
};

/** Where the active thread is remembered; case keeps its pre-existing key. */
export const activeThreadKey = (
  scope: AgentScope,
  agentKind?: AgentKind | null
): string => {
  const base =
    scope.kind === "case"
      ? `caseIde.thread.${scope.id}`
      : `agents.thread.${scope.kind}.${scope.id}`;
  // An internal agent on the scope (the automation builder) remembers its own thread.
  return agentKind ? `${base}.${agentKind}` : base;
};

/** The composer's model picks, as a thread remembers them. */
export type ModelPicks = {
  /** A catalog id; `null` is Auto. */
  model: string | null;
  thinking: boolean;
  optimizeFor: OptimizeFor;
};

/** A fresh browser starts on Auto at the Balanced stop. */
export const DEFAULT_MODEL_PICKS: ModelPicks = {
  model: null,
  thinking: false,
  optimizeFor: "balanced"
};

/**
 * Where the last model pick is remembered across threads (the picker stays
 * where you left it): new threads start from it, except New Chat, which
 * starts on Auto. Versioned so browsers that remembered the old default (a
 * pinned model) start on Auto instead.
 */
export const MODEL_PICK_KEY = "agents.model.v2";

const isOptimizeFor = (value: unknown): value is OptimizeFor =>
  value === "cost" || value === "balanced" || value === "intelligence";

export const readModelPicks = (): ModelPicks => {
  try {
    const raw = window.localStorage.getItem(MODEL_PICK_KEY);
    if (!raw) return DEFAULT_MODEL_PICKS;
    const parsed = JSON.parse(raw) as Partial<{
      model: unknown;
      thinking: unknown;
      optimizeFor: unknown;
    }>;
    return {
      model:
        typeof parsed.model === "string" && parsed.model !== AUTO_MODEL_ID
          ? parsed.model
          : null,
      thinking: parsed.thinking === true,
      optimizeFor: isOptimizeFor(parsed.optimizeFor)
        ? parsed.optimizeFor
        : "balanced"
    };
  } catch {
    return DEFAULT_MODEL_PICKS;
  }
};

export const writeModelPicks = (picks: ModelPicks): void => {
  try {
    window.localStorage.setItem(MODEL_PICK_KEY, JSON.stringify(picks));
  } catch {
    // Not remembered; harmless.
  }
};

/** A thread's picks (older threads have none: Auto). */
const picksOf = (thread: AgentThread): ModelPicks => ({
  model: thread.model && thread.model !== AUTO_MODEL_ID ? thread.model : null,
  thinking: Boolean(thread.thinking),
  optimizeFor: isOptimizeFor(thread.optimize_for)
    ? thread.optimize_for
    : "balanced"
});

/** The picks as the API takes them (`"auto"` for no model). */
const picksUpdate = (picks: ModelPicks): ThreadComposerUpdate => ({
  model: picks.model ?? AUTO_MODEL_ID,
  thinking: picks.thinking,
  optimize_for: picks.optimizeFor
});

export type UseAgentChatOptions = {
  scope: AgentScope;
  /** Who is typing; stamped on outgoing messages so teammates see a name. */
  me?: Me | null;
  /** A thread to open on mount (deep link), overriding the remembered one. */
  initialThreadId?: string | null;
  /**
   * Pin the chat to one thread: never auto-picks another, never writes the
   * remembered `activeThreadKey`, and `send` never creates a thread
   * (onboarding's single conversation). `null` shows an empty transcript.
   */
  fixedThreadId?: string | null;
  /**
   * Talk to an internal agent on the scope (the automation builder on the practice
   * scope) instead of its default: threads are created with this `agent_kind`
   * and the thread list shows only that agent's. Without it the list shows
   * only the scope's default agent's threads.
   */
  agentKind?: AgentKind | null;
  /**
   * Remember the active thread under this key instead of the scope's, and
   * start empty rather than on the newest thread: a surface with its own
   * conversation (the evening review card). `send` still creates a thread.
   */
  threadKey?: string;
  adapters?: ChatAdapters;
};

export const useAgentChat = ({
  scope,
  me,
  initialThreadId,
  fixedThreadId,
  agentKind,
  threadKey,
  adapters
}: UseAgentChatOptions) => {
  const { kind: scopeKind, id: scopeId } = scope;
  const fixed = fixedThreadId !== undefined;
  const rememberAs = (s: AgentScope) =>
    threadKey ?? activeThreadKey(s, agentKind);
  // Which agent's threads this instance shows and creates.
  const wantedAgent: AgentKind = agentKind ?? AGENT_FOR_SCOPE[scopeKind];
  // Threads created before the merge still say `case_assistant` /
  // `client_manager`; they are the assistant's.
  const ownThreads = useCallback(
    (list: AgentThread[]) =>
      list.filter(
        (t) =>
          normalizeAgentKind(
            (t.agent_kind as AnyAgentKind | null | undefined) ?? wantedAgent
          ) === wantedAgent
      ),
    [wantedAgent]
  );
  // A stable identity for effects/callbacks: the scope object may be re-created.
  const scopeRef = useRef(scope);
  scopeRef.current = scope;
  const stableScope = useMemo<AgentScope>(
    () => ({ kind: scopeKind, id: scopeId }),
    [scopeKind, scopeId]
  );

  const [threads, setThreads] = useState<AgentThread[]>([]);
  const [threadsLoading, setThreadsLoading] = useState(true);
  const [threadId, setThreadIdState] = useState<string | null>(() => {
    if (fixed) return fixedThreadId ?? null;
    if (initialThreadId) return initialThreadId;
    try {
      return window.localStorage.getItem(rememberAs(stableScope));
    } catch {
      return null;
    }
  });
  // Pinned instances follow their prop; free ones follow the user.
  const setThreadId = useCallback(
    (next: string | null | ((cur: string | null) => string | null)) => {
      if (fixed) return;
      setThreadIdState(next);
    },
    [fixed]
  );
  useEffect(() => {
    if (fixed) setThreadIdState(fixedThreadId ?? null);
  }, [fixed, fixedThreadId]);
  const [restoring, setRestoring] = useState(false);
  // Same as `restoring`, but flipped synchronously: effects that run in the
  // same commit as the restore effect must not read the stale render value.
  const restoringRef = useRef(false);
  // Threads this client just created, by id: they are empty and their
  // settings are already known, so switching to one needs no fetch.
  const freshThreadsRef = useRef(new Map<string, AgentThread>());
  const [error, setError] = useState<string | null>(null);
  // Someone else's turn running on the active thread (from the thread payload,
  // kept live by `useAgentThreadLive`); own turns are tracked by `status`.
  const [activeTurn, setActiveTurn] = useState<ActiveTurn | null>(null);
  // The thread's message queue, "send now" inbox and stop request, all from
  // the thread payload (kept live the same way as `activeTurn`).
  const [queued, setQueued] = useState<QueuedMessage[]>([]);
  const [steerInbox, setSteerInbox] = useState<SteerItem[]>([]);
  const [stopRequested, setStopRequested] = useState<StopRequested | null>(
    null
  );
  // After Stop (or an error) the queue waits for the person: nothing is
  // dispatched until they send again or pick "Send now".
  const [queuePaused, setQueuePaused] = useState(false);
  // The running turn's subagent steps (`data-step`): transient parts that
  // never enter the message history and are dropped when the turn ends.
  const [turnSteps, setTurnSteps] = useState<StepsByTask>(EMPTY_STEPS);
  const clearTurnState = useCallback(() => setTurnSteps(EMPTY_STEPS), []);
  const meRef = useRef(me);
  meRef.current = me;

  /**
   * Keep the app-wide list of this person's running turns in step with a
   * thread payload: their lock (with a `turn_id`) tracks it, no lock clears
   * it. Applied to every thread update this chat sees, not only the active
   * thread, so a turn left running on another thread is still accounted for.
   */
  const noteRunningTurn = useCallback((thread: AgentThread) => {
    const turn = thread.active_turn;
    const myUid = meRef.current?.uid;
    if (turn?.turn_id && myUid && turn.uid === myUid) {
      runningTurnsStore.track({
        threadId: thread.id,
        turnId: turn.turn_id,
        scope: { kind: thread.scope_kind, id: thread.scope_id },
        title: thread.title || null,
        startedAt: turn.started_at ?? null,
        status: turn.status ?? null,
        statusAt: turn.status_at ?? null
      });
    } else if (!turn) {
      runningTurnsStore.untrack(thread.id);
    }
  }, []);

  /** Mirror a thread payload's queue/steer/stop state for the active thread. */
  const applyTurnState = useCallback(
    (thread: AgentThread) => {
      setActiveTurn(thread.active_turn ?? null);
      setQueued(thread.queued_messages ?? []);
      setSteerInbox(thread.steer_inbox ?? []);
      setStopRequested(thread.stop_requested ?? null);
      noteRunningTurn(thread);
    },
    [noteRunningTurn]
  );

  // While this chat shows a thread, that thread is "on screen": the watcher
  // leaves it to this chat's live updates instead of polling it.
  useEffect(() => {
    if (!threadId) return undefined;
    runningTurnsStore.view(threadId);
    return () => runningTurnsStore.unview(threadId);
  }, [threadId]);
  // Agent | Plan. Seeded from the active thread, persisted with PATCH so a
  // reload lands in the same mode; also sent in every turn's body.
  const [mode, setModeState] = useState<AgentMode>("agent");
  // The plan being built on the active thread, if any (sent as `plan_id`).
  const [planId, setPlanId] = useState<string | null>(null);
  // The composer's skill chip. Orthogonal to the mode and sent every turn.
  // By default it is for the next message only and clears once sent; Pin
  // keeps it on the thread (PATCHed) for every later turn.
  const [skillId, setSkillIdState] = useState<string | null>(null);
  const [skillPinned, setSkillPinnedState] = useState(false);
  // Model / thinking / Optimize For. Seeded from the last pick (localStorage)
  // until a thread loads, then from the thread; persisted with PATCH like the
  // mode and sent in every turn's body.
  const [picks, setPicksState] = useState<ModelPicks>(readModelPicks);

  const adaptersRef = useRef(adapters);
  adaptersRef.current = adapters;
  const modeRef = useRef(mode);
  modeRef.current = mode;
  const planIdRef = useRef(planId);
  planIdRef.current = planId;
  const skillIdRef = useRef(skillId);
  skillIdRef.current = skillId;
  const skillPinnedRef = useRef(skillPinned);
  skillPinnedRef.current = skillPinned;
  /** Set the chip (and whether it is pinned) without telling the server. */
  const showSkill = useCallback((id: string | null, pinned: boolean) => {
    skillIdRef.current = id;
    skillPinnedRef.current = Boolean(id) && pinned;
    setSkillIdState(id);
    setSkillPinnedState(Boolean(id) && pinned);
  }, []);
  // The skill the last message went with: an approval's continuation (no new
  // message) or a retry runs under it after the chip was spent.
  const turnSkillRef = useRef("");
  /** The chip's skill for the request being sent; an unpinned one is spent. */
  const takeSkillForTurn = useCallback(
    (messages: readonly AgentUIMessage[], trigger?: string): string => {
      if (
        trigger === "regenerate-message" ||
        messages[messages.length - 1]?.role !== "user"
      )
        return turnSkillRef.current || skillIdRef.current || "";
      const id = skillIdRef.current ?? "";
      turnSkillRef.current = id;
      if (id && !skillPinnedRef.current) showSkill(null, false);
      return id;
    },
    [showSkill]
  );
  const picksRef = useRef(picks);
  picksRef.current = picks;

  // Switching scope (the panel moved to another client): start over.
  const scopeKeyRef = useRef(`${scopeKind}:${scopeId}`);
  useEffect(() => {
    const key = `${scopeKind}:${scopeId}`;
    if (scopeKeyRef.current === key) return;
    scopeKeyRef.current = key;
    setThreads([]);
    setThreadsLoading(true);
    setActiveTurn(null);
    setQueued([]);
    setSteerInbox([]);
    setStopRequested(null);
    setQueuePaused(false);
    setError(null);
    if (fixed) return;
    let remembered: string | null = null;
    try {
      remembered = window.localStorage.getItem(rememberAs(stableScope));
    } catch {
      remembered = null;
    }
    setThreadIdState(remembered);
  }, [fixed, scopeKind, scopeId, stableScope]);

  useEffect(() => {
    if (fixed) return;
    try {
      if (threadId)
        window.localStorage.setItem(rememberAs(stableScope), threadId);
      else window.localStorage.removeItem(rememberAs(stableScope));
    } catch {
      // Not remembered; harmless.
    }
  }, [fixed, stableScope, threadId]);

  const threadIdRef = useRef(threadId);
  threadIdRef.current = threadId;

  // `attachToTurn`, defined once the chat helpers exist (below).
  const attachRef = useRef<(thread: AgentThread) => void>(() => {});
  // The running turn last attached to (`attachToTurn`), and when.
  const attachedTurnRef = useRef<{ key: string; at: number } | null>(null);
  // The thread whose stream last ended with its finish part, and when.
  const cleanFinishRef = useRef<{ threadId: string; at: number } | null>(null);

  const refreshThreads = useCallback(async () => {
    try {
      const list = ownThreads(await AgentAPI.listThreads(stableScope));
      setThreads(list);
      const current = list.find((t) => t.id === threadIdRef.current);
      if (current) {
        applyTurnState(current);
        // A turn still running that this client is not following (its
        // stream was cut, or the `/events` channel reconnected after
        // missing the lock): follow it. Not the turn that just finished
        // here, whose lock the server is about to release. Not while the
        // transcript is still being restored either: the restore attaches
        // after its `setMessages`, which would otherwise land on the live tail.
        const clean = cleanFinishRef.current;
        const justFinished =
          clean?.threadId === current.id &&
          Date.now() - clean.at < CLEAN_FINISH_GRACE_MS;
        if (!justFinished && !restoringRef.current) attachRef.current(current);
      }
      return list;
    } catch (e) {
      setError((e as Error).message ?? "Couldn't load conversations");
      return [];
    } finally {
      setThreadsLoading(false);
    }
  }, [applyTurnState, ownThreads, stableScope]);

  // First load: list threads and fall back to the newest when none is remembered
  // (a pinned instance keeps its thread whatever the list says).
  useEffect(() => {
    let cancelled = false;
    refreshThreads().then((list) => {
      if (cancelled || fixed) return;
      setThreadIdState((cur) =>
        cur && list.some((t) => t.id === cur)
          ? cur
          : threadKey
          ? null
          : list[0]?.id ?? null
      );
    });
    return () => {
      cancelled = true;
    };
  }, [fixed, refreshThreads]);

  // The agent member the composer is talking to (from the last scoped mention).
  const responderRef = useRef<ResponderRef | null>(null);

  // The open `/stream` tail, if any. A resume aborts it first, so a tab holds
  // at most one tail per thread; the server otherwise keeps every abandoned
  // one open until the turn ends.
  const streamTailRef = useRef<AbortController | null>(null);
  const abortStreamTail = useCallback(() => {
    streamTailRef.current?.abort();
    streamTailRef.current = null;
  }, []);
  const transport = useMemo(
    () =>
      threadId
        ? new DefaultChatTransport<AgentUIMessage>({
            api: agentChatUrl(stableScope, threadId),
            headers: async (): Promise<Record<string, string>> => {
              const token = await getAuthToken();
              return token ? { Authorization: `Bearer ${token}` } : {};
            },
            fetch: (input, init) => {
              const url =
                typeof input === "string"
                  ? input
                  : input instanceof URL
                  ? input.href
                  : input.url;
              if (url !== agentStreamUrl(stableScope, threadId)) {
                return fetch(input, init);
              }
              streamTailRef.current?.abort();
              const tail = new AbortController();
              init?.signal?.addEventListener("abort", () => tail.abort(), {
                once: true
              });
              streamTailRef.current = tail;
              return fetch(input, { ...init, signal: tail.signal });
            },
            prepareSendMessagesRequest: ({
              messages,
              id,
              trigger,
              messageId,
              body
            }) => ({
              body: {
                id,
                trigger,
                messageId,
                messages,
                mode: modeRef.current,
                plan_id: planIdRef.current,
                // The chip is the truth: "" clears a skill the thread still has.
                skill_id: takeSkillForTurn(messages, trigger),
                model: picksRef.current.model ?? AUTO_MODEL_ID,
                thinking: picksRef.current.thinking,
                optimize_for: picksRef.current.optimizeFor,
                responder: responderRef.current,
                selection: adaptersRef.current?.selection?.() ?? null,
                // Per-send extras: `queued_id` (and the pick it was queued
                // with) when dispatching the queue head.
                ...(body ?? {})
              }
            }),
            // `resumeStream` (a turn still running when the thread is
            // opened): the server replays the turn from its first part and
            // follows it live, or answers 204 when nothing is running.
            prepareReconnectToStreamRequest: ({ headers }) => ({
              api: agentStreamUrl(stableScope, threadId),
              headers
            })
          })
        : undefined,
    [stableScope, takeSkillForTurn, threadId]
  );

  // The latest `useChat` helpers, for callbacks (including `useChat`'s own
  // `onError`) that must not go stale. Filled right after the hook below.
  const chatRef = useRef({} as UseChatHelpers<AgentUIMessage>);
  // Reloads the thread from the server; set once the helpers it needs exist.
  const resyncRef = useRef<() => void>(() => {});
  const chat = useChat<AgentUIMessage>({
    id: threadId ?? "no-thread",
    transport,
    sendAutomaticallyWhen,
    // Coalesce stream updates: text arrives token by token, and a re-render per
    // token is what makes a long reply feel sluggish.
    experimental_throttle: STREAM_THROTTLE_MS,
    onData: (part) => {
      const host = adaptersRef.current;
      if (part.type === "data-step") {
        const step = part.data;
        if (step?.taskCallId && step.callId) {
          setTurnSteps((cur) => {
            const rows = cur[step.taskCallId] ?? [];
            const at = rows.findIndex((r) => r.callId === step.callId);
            const next =
              at === -1
                ? [...rows, step]
                : rows.map((r, i) => (i === at ? { ...r, ...step } : r));
            return { ...cur, [step.taskCallId]: next };
          });
        }
      } else if (part.type === "data-invalidate") {
        notifyTimeInvalidated(part.data.targets ?? []);
        notifyCompaniesInvalidated(part.data.targets ?? []);
        notifySettingsInvalidated(part.data.targets ?? []);
        notifyIntakeTemplatesInvalidated(part.data.targets ?? []);
        host?.onInvalidate?.(part.data.targets ?? []);
      } else if (part.type === "data-mode") {
        // An approved `request_mode_switch`: the server already persisted it.
        const next = part.data?.mode;
        if (next === "agent" || next === "plan") {
          modeRef.current = next;
          setModeState(next);
        }
      } else if (part.type === "data-open-artifact") {
        const a = part.data;
        // A support handoff is shown by its tool call's card, never opened.
        if (a?.kind === "support") return;
        // A firm intake template opens in Firm Knowledge, not in a host pane.
        const kind: string | undefined = a?.kind;
        if (kind === "template") {
          if (a?.id) notifyIntakeTemplateOpened(String(a.id));
          return;
        }
        if (a?.kind && a.id)
          host?.onOpenArtifact?.({
            kind: a.kind,
            id: String(a.id),
            title: a.title ?? host?.titleFor?.(a),
            page: a.page ?? null,
            building: a.building === true
          });
      } else if (part.type === "data-client-tool") {
        const request = part.data;
        const thread = threadIdRef.current;
        if (request?.id && thread) {
          // The tool blocks server-side until this lands (or times out); a
          // host without an editor answers "not here" so it falls back.
          const run = host?.onClientTool
            ? host.onClientTool(request)
            : Promise.resolve<ClientToolOutcomeData>({
                ok: false,
                error: "no editor is open in this client",
                detail: { fallback: true }
              });
          run
            .catch(
              (error): ClientToolOutcomeData => ({
                ok: false,
                error:
                  error instanceof Error
                    ? error.message
                    : "the editor failed to run this"
              })
            )
            .then((outcome) =>
              AgentAPI.postClientToolResult(
                stableScope,
                thread,
                request.id,
                outcome
              )
            )
            .catch(() => undefined);
        }
      } else if (part.type === "data-handoff") {
        if (part.data?.thread_id) host?.onHandoff?.(part.data);
      } else if (part.type === "data-change") {
        // A recorded, undoable write. The part stays in the message for the
        // Undo card; the tool result's own `data-invalidate` refreshes caches.
      } else if (part.type === "data-turn-context") {
        const { user_message_id: messageId, ...context } = part.data;
        const { current } = chatRef;
        const at = messageId
          ? current.messages.findIndex((m) => m.id === messageId)
          : current.messages.findLastIndex((m) => m.role === "user");
        if (at !== -1 && current.messages[at].role === "user")
          current.setMessages(
            current.messages.map((m, i) =>
              i === at
                ? { ...m, metadata: { ...(m.metadata ?? {}), context } }
                : m
            )
          );
      } else if (part.type === "data-queue-next") {
        // The turn ended cleanly and a queued message is next. The dispatch
        // effect below sends it once `status` settles; nothing to do here.
      } else if (part.type === "data-stopped") {
        setQueuePaused(true);
      }
    },
    onError: (e) => {
      clearTurnState();
      if (isToolCallMismatch(e.message)) {
        chatRef.current.clearError();
        resyncRef.current();
        return;
      }
      const conflict = parseTurnInProgress(e.message);
      if (conflict) {
        setActiveTurn(conflict.activeTurn);
        if (conflict.queued) {
          // The server held the message: it belongs in the queue, not the
          // transcript. Drop the optimistic row; the thread update lists it.
          const { current } = chatRef;
          const last = current.messages[current.messages.length - 1];
          if (last?.role === "user")
            current.setMessages(current.messages.slice(0, -1));
          setQueued((cur) =>
            cur.some((q) => q.id === conflict.queued?.id)
              ? cur
              : [...cur, conflict.queued as QueuedMessage]
          );
          current.clearError();
          return;
        }
        toast.warning(conflict.error, {
          description: "Your message was not sent. Try again when they're done."
        });
        setError(conflict.error);
        return;
      }
      const restart = parseServerRestart(e.message);
      if (restart?.resuming) {
        // The server is going away under this turn and the worker pool picks
        // it up from its checkpoint. Not a failure: keep the user's row and
        // the partial reply (the finished message replaces it by id over the
        // realtime channel), leave the queue running, and show the lock as
        // continuing until the thread update carries the worker's lock.
        chatRef.current.clearError();
        const who = meRef.current;
        const now = new Date().toISOString();
        setActiveTurn((cur) =>
          cur
            ? { ...cur, interrupted_at: cur.interrupted_at ?? now }
            : {
                uid: who?.uid ?? "",
                name: who?.name || who?.email || "You",
                started_at: now,
                interrupted_at: now
              }
        );
        return;
      }
      if (restart) {
        // Ended for good: the partial reply stays, with the "send again" note.
        setQueuePaused(true);
        setError(restart.error);
        return;
      }
      if (isQueueOutOfOrder(e.message)) {
        // Someone (the worker, or another client) already ran that queued
        // message; the transcript will show it. Drop our optimistic copy.
        const { current } = chatRef;
        const last = current.messages[current.messages.length - 1];
        if (last?.role === "user")
          current.setMessages(current.messages.slice(0, -1));
        current.clearError();
        void refreshThreads();
        return;
      }
      setQueuePaused(true);
      setError(e.message);
    },
    onFinish: (end) => {
      clearTurnState();
      const id = threadIdRef.current;
      if (!streamWasCut(end)) {
        if (id) cleanFinishRef.current = { threadId: id, at: Date.now() };
        void refreshThreads();
        return;
      }
      // Cut before the finish part: the turn may still be running on the
      // server. The refresh reattaches to it (the replay replaces the
      // partial reply by id); a turn that ended answers 204.
      cleanFinishRef.current = null;
      attachedTurnRef.current = null;
      Sentry.addBreadcrumb({
        category: "agent.stream",
        level: "warning",
        message: "agent stream ended without a finish part",
        data: { threadId: id, isDisconnect: end.isDisconnect }
      });
      if (end.isDisconnect) {
        chatRef.current.clearError();
        setError(null);
        setQueuePaused(false);
      }
      void refreshThreads();
    }
  });
  chatRef.current = chat;

  // Nothing is running: no live steps to show.
  useEffect(() => {
    if (chat.status === "ready" || chat.status === "error") clearTurnState();
  }, [chat.status, clearTurnState]);

  /**
   * Reattach to a turn running without us. A turn runs on the server
   * detached from the browser that sent it, so a thread opened mid-turn (a
   * reload, coming back from another page, a teammate's send, our own turn
   * continued on the worker pool) is caught up with `resumeStream`: the
   * server replays the turn from its first part and follows it live, or
   * answers 204 when it has already ended. Guarded by turn id: the same
   * turn is attached once, however many thread updates announce it.
   */
  const attachToTurn = useCallback((thread: AgentThread) => {
    const turnId = thread.active_turn?.turn_id ?? null;
    if (!turnId || thread.id !== threadIdRef.current) return;
    const { current } = chatRef;
    if (current.status !== "ready" && current.status !== "error") return;
    const key = `${thread.id}:${turnId}`;
    const attached = attachedTurnRef.current;
    // The same turn again: only once its tail has been gone a while, so a
    // tail that errors at once is not reopened on every thread update.
    if (
      attached?.key === key &&
      Date.now() - attached.at < REATTACH_SAME_TURN_MS
    )
      return;
    attachedTurnRef.current = { key, at: Date.now() };
    // The reply so far, persisted at a hand-off or a step checkpoint: the
    // replay carries every part of the turn, so the partial copy would
    // double them.
    const last = current.messages[current.messages.length - 1];
    if (
      last?.role === "assistant" &&
      (last.metadata?.interrupted || last.metadata?.checkpoint)
    )
      current.setMessages(current.messages.slice(0, -1));
    void current.resumeStream();
  }, []);
  attachRef.current = attachToTurn;
  // Leaving the thread (or the page) closes its tail.
  useEffect(() => abortStreamTail, [abortStreamTail, threadId]);

  /** Seed the composer (mode, plan, model picks) from a thread. */
  const applyThreadSettings = useCallback((thread: AgentThread) => {
    const next: AgentMode = thread.mode === "plan" ? "plan" : "agent";
    modeRef.current = next;
    setModeState(next);
    planIdRef.current = thread.active_plan_id ?? null;
    setPlanId(thread.active_plan_id ?? null);
    showSkill(thread.skill_id ?? null, Boolean(thread.skill_pinned));
    const threadPicks = picksOf(thread);
    picksRef.current = threadPicks;
    setPicksState(threadPicks);
    setQueuePaused(false);
  }, []);

  resyncRef.current = () => {
    const id = threadIdRef.current;
    if (!id) return;
    AgentAPI.getThread(stableScope, id)
      .then(({ thread, messages }) => {
        if (threadIdRef.current !== id) return;
        chatRef.current.setMessages(messages as AgentUIMessage[]);
        applyTurnState(thread);
        attachToTurn(thread);
      })
      .catch(() => {
        if (threadIdRef.current === id)
          setError("Couldn't load this conversation.");
      });
  };

  // Restore the transcript (and the thread's mode) whenever the active thread changes.
  useEffect(() => {
    // A restore that was cancelled by this switch leaves nothing behind.
    restoringRef.current = false;
    setRestoring(false);
    if (!threadId) {
      chatRef.current.setMessages([]);
      return undefined;
    }
    // A thread this client just created: empty, settings in hand (its lock and
    // queue were reset when it was opened). Nothing to fetch, so the first
    // message is never overwritten by a late restore and no "Restoring"
    // loader flashes over an empty conversation.
    const fresh = freshThreadsRef.current.get(threadId);
    if (fresh) {
      freshThreadsRef.current.delete(threadId);
      chatRef.current.setMessages([]);
      // An unpinned skill is not stored on the thread; it rides the first
      // message. Seeding the composer from this empty thread must not clear
      // it before that send (`takeSkillForTurn`) reads it.
      const ridingSkill = fresh.skill_id ? null : skillIdRef.current;
      const ridingPinned = skillPinnedRef.current;
      applyThreadSettings(fresh);
      if (ridingSkill) showSkill(ridingSkill, ridingPinned);
      return undefined;
    }
    let cancelled = false;
    restoringRef.current = true;
    setRestoring(true);
    AgentAPI.getThread(stableScope, threadId)
      .then(({ thread, messages }) => {
        if (cancelled) return;
        chatRef.current.setMessages(messages as AgentUIMessage[]);
        applyThreadSettings(thread);
        applyTurnState(thread);
        // Opened mid-turn: pick the stream up where it is.
        attachToTurn(thread);
      })
      .catch(() => {
        if (!cancelled) setError("Couldn't load this conversation.");
      })
      .finally(() => {
        if (cancelled) return;
        restoringRef.current = false;
        setRestoring(false);
      });
    return () => {
      cancelled = true;
    };
  }, [
    applyThreadSettings,
    applyTurnState,
    attachToTurn,
    showSkill,
    stableScope,
    threadId
  ]);

  /** Create a thread with the composer's current mode and picks; no switch. */
  const createThread = useCallback(async () => {
    let t = await AgentAPI.createThread(
      stableScope,
      agentKind ? { agentKind } : undefined
    );
    // A new conversation keeps the mode and model picks the attorney is
    // working with (the picker stays where they left it). A skill rides the
    // first message's body; only a pin is remembered on the thread.
    const seed = picksRef.current;
    const updates: ThreadComposerUpdate = {
      ...(modeRef.current !== "agent" ? { mode: modeRef.current } : {}),
      ...(skillIdRef.current && skillPinnedRef.current
        ? { skill_id: skillIdRef.current, skill_pinned: true }
        : {}),
      ...(seed.model ||
      seed.thinking ||
      seed.optimizeFor !== DEFAULT_MODEL_PICKS.optimizeFor
        ? picksUpdate(seed)
        : {})
    };
    if (Object.keys(updates).length) {
      try {
        t = await AgentAPI.updateThread(stableScope, t.id, updates);
      } catch {
        // The first turn's body carries the picks anyway.
      }
    }
    return t;
  }, [agentKind, stableScope]);

  /** Make a just-created thread the active one. */
  const openCreated = useCallback(
    (t: AgentThread) => {
      // Empty and fully known: the restore effect seeds from this, no fetch.
      // Its lock and queue (empty) are applied here, at the switch, so a
      // message queued behind the first one right after is not wiped.
      // (A pinned instance does not switch to it, so nothing to seed.)
      if (!fixed) {
        freshThreadsRef.current.set(t.id, t);
        applyTurnState(t);
      }
      setThreads((cur) => [t, ...cur]);
      setThreadId(t.id);
    },
    [applyTurnState, fixed, setThreadId]
  );

  const newThread = useCallback(async () => {
    const t = await createThread();
    openCreated(t);
    return t;
  }, [createThread, openCreated]);

  /**
   * New Chat: an empty conversation that becomes a thread with its first
   * message, so an abandoned one leaves no thread and no activity row.
   */
  const startNewThread = useCallback(() => {
    // A new conversation starts without a skill, and on Auto: a model named
    // for one conversation should not carry into the next.
    showSkill(null, false);
    const auto: ModelPicks = {
      ...DEFAULT_MODEL_PICKS,
      optimizeFor: picksRef.current.optimizeFor
    };
    if (JSON.stringify(picksRef.current) !== JSON.stringify(auto)) {
      picksRef.current = auto;
      setPicksState(auto);
      writeModelPicks(auto);
    }
    setActiveTurn(null);
    setQueued([]);
    setSteerInbox([]);
    setStopRequested(null);
    setQueuePaused(false);
    setError(null);
    setThreadId(null);
  }, [setThreadId, showSkill]);

  /** The composer's selector changed. */
  const setMode = useCallback(
    (next: AgentMode) => {
      if (next === modeRef.current) return;
      modeRef.current = next;
      setModeState(next);
      if (threadId) {
        AgentAPI.updateThreadMode(stableScope, threadId, next).catch(() => {
          // The next turn's body carries the mode; the PATCH is only for reloads.
        });
      }
    },
    [stableScope, threadId]
  );

  /**
   * The composer's skill chip changed (`null` clears it). A new pick is for
   * the next message; the server only hears of it when a pin is dropped.
   */
  const setSkill = useCallback(
    (next: string | null) => {
      const wanted = next || null;
      if (wanted === skillIdRef.current) return;
      const wasPinned = skillPinnedRef.current;
      showSkill(wanted, false);
      if (threadId && wasPinned) {
        AgentAPI.updateThread(stableScope, threadId, {
          skill_id: wanted ?? "",
          skill_pinned: false
        }).catch(() => {
          // The next turn's body carries the skill; the PATCH is only for reloads.
        });
      }
    },
    [showSkill, stableScope, threadId]
  );

  /** Pin keeps the chip's skill for every later turn; Unpin, the next message only. */
  const setSkillPinned = useCallback(
    (pinned: boolean) => {
      const id = skillIdRef.current;
      if (!id || pinned === skillPinnedRef.current) return;
      showSkill(id, pinned);
      if (threadId) {
        AgentAPI.updateThread(stableScope, threadId, {
          skill_id: id,
          skill_pinned: pinned
        }).catch(() => {
          // A new thread is created with the pin; this PATCH is for existing ones.
        });
      }
    },
    [showSkill, stableScope, threadId]
  );

  /**
   * A model pick changed: shown at once, remembered for new threads, and
   * PATCHed onto the thread so a reload (and teammates) land on it.
   */
  const setPicks = useCallback(
    (patch: Partial<ModelPicks>) => {
      const next: ModelPicks = { ...picksRef.current, ...patch };
      if (
        next.model === picksRef.current.model &&
        next.thinking === picksRef.current.thinking &&
        next.optimizeFor === picksRef.current.optimizeFor
      )
        return;
      picksRef.current = next;
      setPicksState(next);
      writeModelPicks(next);
      if (threadId) {
        AgentAPI.updateThread(stableScope, threadId, picksUpdate(next)).catch(
          () => {
            // The next turn's body carries the picks; the PATCH is for reloads.
          }
        );
      }
    },
    [stableScope, threadId]
  );
  const setModel = useCallback(
    (model: string | null) =>
      setPicks({ model: model === AUTO_MODEL_ID ? null : model }),
    [setPicks]
  );
  const setThinking = useCallback(
    (thinking: boolean) => setPicks({ thinking }),
    [setPicks]
  );
  const setOptimizeFor = useCallback(
    (optimizeFor: OptimizeFor) => setPicks({ optimizeFor }),
    [setPicks]
  );

  const deleteThread = useCallback(
    async (id: string) => {
      await AgentAPI.deleteThread(stableScope, id);
      const rest = threads.filter((t) => t.id !== id);
      setThreads(rest);
      // Read through the ref: the caller may have moved off the thread while
      // the request was in flight (the Case IDE closes its tab first).
      if (threadIdRef.current === id) setThreadId(rest[0]?.id ?? null);
    },
    [setThreadId, stableScope, threads]
  );

  /**
   * Rename / pin / archive: applied to the list right away and reverted if the
   * server says no, so the rail feels instant.
   */
  const updateThreadMeta = useCallback(
    async (id: string, updates: ThreadMetaUpdate) => {
      let previous: AgentThread | undefined;
      setThreads((cur) => {
        previous = cur.find((t) => t.id === id);
        return cur.map((t) => (t.id === id ? { ...t, ...updates } : t));
      });
      try {
        const saved = await AgentAPI.updateThread(stableScope, id, updates);
        setThreads((cur) => cur.map((t) => (t.id === id ? saved : t)));
        return saved;
      } catch (e) {
        const before = previous;
        if (before)
          setThreads((cur) => cur.map((t) => (t.id === id ? before : t)));
        setError((e as Error).message ?? "Couldn't update the conversation");
        throw e;
      }
    },
    [stableScope]
  );
  const renameThread = useCallback(
    (id: string, title: string) =>
      updateThreadMeta(id, { title: title.trim() }),
    [updateThreadMeta]
  );
  const setPinned = useCallback(
    (id: string, pinned: boolean) => updateThreadMeta(id, { pinned }),
    [updateThreadMeta]
  );
  const setArchived = useCallback(
    (id: string, archived: boolean) => updateThreadMeta(id, { archived }),
    [updateThreadMeta]
  );

  /**
   * A message waiting for its thread's transport (the thread was just created,
   * or `buildPlan` switched to another one). Sent by the effect below once the
   * thread is active and restored.
   */
  const pendingSend = useRef<{
    threadId: string;
    id?: string;
    text: string;
    metadata?: AgentMessageMetadata;
    /** Editor context captured when Enter was pressed. */
    selection?: SerializedSelection | null;
    files?: AttachmentFilePart[];
    /** Request-body overrides for this turn (`sendOnThread`'s mode and skill). */
    body?: Record<string, unknown>;
  } | null>(null);
  /**
   * The first message of a conversation, shown from the moment Enter is
   * pressed while its thread is still being created (Cursor shows the row and
   * "Planning next moves" at once). Replaced by the real message, same id,
   * when sent.
   */
  const [pendingFirst, setPendingFirst] = useState<AgentUIMessage | null>(null);
  const pendingFirstRef = useRef(pendingFirst);
  pendingFirstRef.current = pendingFirst;
  /** The thread creation a first message is waiting on. */
  const creatingRef = useRef<Promise<AgentThread | null> | null>(null);

  /** Author + mentions + audience for an outgoing message. */
  const stamp = useCallback(
    (
      text: string,
      audience: "agent" | "team",
      extra?: AgentMessageMetadata
    ): AgentMessageMetadata => {
      const parsed = parseMentions(text);
      const mentions: MentionRef[] = [
        ...parsed.users,
        ...parsed.agents.map((a) => ({
          kind: "agent" as const,
          id: `${a.scopeKind}:${a.scopeId}`,
          label: a.label
        })),
        ...(parsed.agentMentioned && !parsed.agents.length
          ? [{ kind: "agent" as const, id: "agent", label: ASSISTANT_PERSONA }]
          : [])
      ];
      const who = meRef.current;
      const first = parsed.agents[0];
      return {
        ...(extra ?? {}),
        ...(who?.uid
          ? {
              author: {
                uid: who.uid,
                name: who.name || who.email || who.uid,
                email: who.email ?? null,
                avatar_url: who.avatarUrl ?? null
              }
            }
          : {}),
        mentions,
        audience,
        ...(audience === "agent" && first
          ? {
              responder: {
                scope_kind: first.scopeKind,
                scope_id: first.scopeId
              }
            }
          : {})
      };
    },
    []
  );

  /** A people-only message: persist it, show it, no agent turn. */
  const postTeamMessage = useCallback(
    async (targetThreadId: string, text: string) => {
      const id = `user_${crypto.randomUUID().replace(/-/g, "")}`;
      // Optimistic row; replaced by the stored copy (server-side author) below.
      const optimistic: AgentUIMessage = {
        id,
        role: "user",
        parts: [{ type: "text", text }],
        metadata: stamp(text, "team")
      };
      chatRef.current.setMessages([...chatRef.current.messages, optimistic]);
      try {
        const { message, thread } = await AgentAPI.postTeamMessage(
          stableScope,
          targetThreadId,
          text,
          id
        );
        chatRef.current.setMessages(
          chatRef.current.messages.map((m) =>
            m.id === id ? (message as AgentUIMessage) : m
          )
        );
        setThreads((cur) => [thread, ...cur.filter((t) => t.id !== thread.id)]);
      } catch (e) {
        chatRef.current.setMessages(
          chatRef.current.messages.filter((m) => m.id !== id)
        );
        setError((e as Error).message ?? "Couldn't send your message");
      }
    },
    [stableScope, stamp]
  );

  /**
   * Send text, creating a thread first when there is none yet. Slack rule:
   * mentioning only people posts a team message; naming an agent (or nobody)
   * asks one: a scoped agent named, else the thread's own agent. The
   * responder travels in the request body (`responder`) as well as in the
   * message metadata so the server can validate it.
   */
  const activeTurnRef = useRef(activeTurn);
  activeTurnRef.current = activeTurn;
  const queuedRef = useRef(queued);
  queuedRef.current = queued;

  /**
   * A turn is running on the active thread: ours (streaming, or a first
   * message whose thread is still being created) or someone's.
   */
  const turnBusy = useCallback(() => {
    const s = chatRef.current.status;
    return (
      s === "submitted" ||
      s === "streaming" ||
      pendingFirstRef.current !== null ||
      activeTurnRef.current !== null
    );
  }, []);

  /**
   * Hold a message for after the running turn (Cursor's Enter-while-busy).
   * The queue lives on the thread so everyone sees it; the item is shown
   * locally right away and confirmed by the thread update.
   */
  const enqueue = useCallback(
    async (
      text: string,
      files: AttachmentFilePart[] = [],
      options: { build?: BuildStamp } = {}
    ) => {
      const trimmed = text.trim();
      if (!trimmed && !files.length) return null;
      // The message the running turn is answering, sent again because the
      // reply looked stuck: it is already running, not next.
      if (
        !files.length &&
        turnBusy() &&
        lastUserText(chatRef.current.messages) === normalized(trimmed)
      ) {
        toast("That message is already running.");
        return null;
      }
      // Enter again while the first message's thread is still being created:
      // wait for it, then queue there (its first turn is about to start).
      let target = threadId;
      if (!target && creatingRef.current) {
        target = (await creatingRef.current)?.id ?? null;
      }
      if (!target) return null;
      setQueuePaused(false);
      const id = `user_${crypto.randomUUID().replace(/-/g, "")}`;
      const who = meRef.current;
      const optimistic: QueuedMessage = {
        id: `pending_${id}`,
        uid: who?.uid ?? "",
        name: who?.name || who?.email || "You",
        text: trimmed,
        message_id: id,
        ...(files.length ? { attachments: files } : {}),
        ...(options.build ? { build: options.build } : {}),
        created_at: new Date().toISOString()
      };
      setQueued((cur) => [...cur, optimistic]);
      try {
        const { queued: item, thread } = await AgentAPI.enqueueMessage(
          stableScope,
          target,
          trimmed,
          {
            id,
            mode: modeRef.current,
            model: picksRef.current.model ?? AUTO_MODEL_ID,
            thinking: picksRef.current.thinking,
            selection: adaptersRef.current?.selection?.() ?? null,
            attachments: files,
            build: options.build ?? null
          }
        );
        setQueued(thread.queued_messages ?? []);
        return item;
      } catch (e) {
        setQueued((cur) => cur.filter((q) => q.id !== optimistic.id));
        setError((e as Error).message ?? "Couldn't queue your message");
        return null;
      }
    },
    [stableScope, threadId, turnBusy]
  );

  const send = useCallback(
    async (
      text: string,
      metadata?: AgentMessageMetadata,
      files: AttachmentFilePart[] = []
    ) => {
      const trimmed = text.trim();
      if (!trimmed && !files.length) return;
      keepActiveTab();
      noteAgentTurnSent();
      const parsed = parseMentions(trimmed);
      const toAgent = parsed.addressedToAgent;
      // Files are for an agent; a people-only message cannot carry them.
      if (!toAgent && files.length) {
        setError("Files go to the agent: mention it to send them");
        return;
      }
      const first = parsed.agents[0];
      responderRef.current = first
        ? { scope_kind: first.scopeKind, scope_id: first.scopeId }
        : null;
      if (!threadId) {
        if (fixed) return; // a pinned instance without a thread cannot post
        if (creatingRef.current) {
          // A first message is already on its way: this one waits behind it.
          await enqueue(trimmed, files);
          return;
        }
        if (!toAgent) {
          const t = await newThread();
          await postTeamMessage(t.id, trimmed);
          return;
        }
        // Shown at once, with the turn status, while the thread is created;
        // the real send (same id) replaces the row when the transport exists.
        const id = `user_${crypto.randomUUID().replace(/-/g, "")}`;
        const stamped = stamp(trimmed, "agent", metadata);
        const selection = adaptersRef.current?.selection?.() ?? null;
        const optimistic: AgentUIMessage = {
          id,
          role: "user",
          parts: messageParts(trimmed, files),
          metadata: stamped
        };
        setPendingFirst(optimistic);
        setQueuePaused(false);
        const creating = createThread()
          .then((t) => {
            // Stopped while the thread was being created: open it, send nothing.
            if (pendingFirstRef.current?.id === id)
              pendingSend.current = {
                threadId: t.id,
                id,
                text: trimmed,
                metadata: stamped,
                selection,
                files
              };
            // The transport for the new thread is built on the next render;
            // the effect below sends once it is. The pending send is in place
            // before the switch so that render cannot miss it.
            openCreated(t);
            return t;
          })
          .catch((e: unknown) => {
            setPendingFirst(null);
            setError(
              (e as Error)?.message ?? "Couldn't start the conversation"
            );
            return null;
          })
          .finally(() => {
            creatingRef.current = null;
          });
        creatingRef.current = creating;
        await creating;
        return;
      }
      if (!toAgent) {
        await postTeamMessage(threadId, trimmed);
        return;
      }
      // A turn is running (ours or a teammate's): the message waits its turn
      // rather than racing the lock. Same for messages behind an existing
      // queue, so send order is kept.
      if (turnBusy() || queuedRef.current.length > 0) {
        await enqueue(trimmed, files);
        return;
      }
      setQueuePaused(false);
      await chatRef.current.sendMessage(
        files.length
          ? {
              role: "user",
              parts: messageParts(trimmed, files),
              metadata: stamp(trimmed, "agent", metadata)
            }
          : { text: trimmed, metadata: stamp(trimmed, "agent", metadata) }
      );
    },
    [
      createThread,
      enqueue,
      fixed,
      newThread,
      openCreated,
      postTeamMessage,
      stamp,
      threadId,
      turnBusy
    ]
  );

  /**
   * Send a queued message as a turn of its own (`queued_id` lets the server
   * take it off the queue as the turn begins, or refuse a stale dispatch).
   */
  const dispatchQueued = useCallback(
    async (item: QueuedMessage) => {
      if (!threadId) return;
      const parsed = parseMentions(item.text);
      const first = parsed.agents[0];
      responderRef.current = first
        ? { scope_kind: first.scopeKind, scope_id: first.scopeId }
        : null;
      if (item.mode === "agent" || item.mode === "plan") {
        modeRef.current = item.mode;
        setModeState(item.mode);
      }
      // The message runs with the pick it was queued with (the composer may
      // have moved on since); the body carries it explicitly.
      const picked = typeof item.model === "string" && item.model !== "";
      setQueued((cur) => cur.filter((q) => q.id !== item.id));
      await chatRef.current.sendMessage(
        {
          ...(item.message_id ? { id: item.message_id } : {}),
          role: "user",
          parts: messageParts(item.text, item.attachments ?? []),
          metadata: stamp(
            item.text,
            "agent",
            item.build ? { build: item.build } : undefined
          )
        },
        {
          body: {
            queued_id: item.id,
            ...(picked ? { model: item.model } : {}),
            ...(typeof item.thinking === "boolean"
              ? { thinking: item.thinking }
              : {})
          }
        }
      );
    },
    [stamp, threadId]
  );

  /**
   * "Send now" (Cursor's ⌘⏎): deliver `text` into the running turn before its
   * next step. With no turn running it is simply sent. A message with files
   * cannot join a running turn; it is queued to run next instead.
   */
  const sendNow = useCallback(
    async (
      text: string,
      metadata?: AgentMessageMetadata,
      files: AttachmentFilePart[] = []
    ) => {
      const trimmed = text.trim();
      if (!trimmed && !files.length) return;
      setQueuePaused(false);
      if (!threadId || !turnBusy()) {
        await send(trimmed, metadata, files);
        return;
      }
      if (files.length) {
        const item = await enqueue(trimmed, files);
        const rest = queuedRef.current.filter((q) => q.id !== item?.id);
        if (item && rest.length) {
          try {
            const thread = await AgentAPI.reorderQueued(stableScope, threadId, [
              item.id,
              ...rest.map((q) => q.id)
            ]);
            setQueued(thread.queued_messages ?? []);
          } catch {
            // It still runs, after the messages queued before it.
          }
        }
        return;
      }
      const id = `user_${crypto.randomUUID().replace(/-/g, "")}`;
      try {
        const { thread } = await AgentAPI.steerThread(
          stableScope,
          threadId,
          trimmed,
          id
        );
        setSteerInbox(thread.steer_inbox ?? []);
      } catch (e) {
        if (isNoTurnRunning(e)) {
          await send(trimmed, metadata);
          return;
        }
        setError((e as Error).message ?? "Couldn't send your message");
      }
    },
    [enqueue, send, stableScope, threadId, turnBusy]
  );

  /** "Send now" on a queued message. */
  const sendQueuedNow = useCallback(
    async (qid: string) => {
      if (!threadId) return;
      const item = queuedRef.current.find((q) => q.id === qid);
      if (!item) return;
      setQueuePaused(false);
      // A message with files runs as its own turn (it cannot join the running
      // one), so "send now" moves it to the head of the queue instead.
      if (turnBusy() && item.attachments?.length) {
        const ids = queuedRef.current.map((q) => q.id);
        if (ids[0] === qid) return;
        try {
          const thread = await AgentAPI.reorderQueued(stableScope, threadId, [
            qid,
            ...ids.filter((i) => i !== qid)
          ]);
          setQueued(thread.queued_messages ?? []);
        } catch (e) {
          setError((e as Error).message ?? "Couldn't send that message");
        }
        return;
      }
      if (turnBusy()) {
        try {
          const { thread } = await AgentAPI.sendQueuedNow(
            stableScope,
            threadId,
            qid
          );
          setQueued(thread.queued_messages ?? []);
          setSteerInbox(thread.steer_inbox ?? []);
          return;
        } catch (e) {
          if (!isNoTurnRunning(e)) {
            setError((e as Error).message ?? "Couldn't send that message");
            return;
          }
        }
      }
      // Idle: run it as a turn. It must be the head, so move it there first.
      const ids = queuedRef.current.map((q) => q.id);
      if (ids[0] !== qid) {
        const order = [qid, ...ids.filter((i) => i !== qid)];
        try {
          const thread = await AgentAPI.reorderQueued(
            stableScope,
            threadId,
            order
          );
          setQueued(thread.queued_messages ?? []);
        } catch (e) {
          setError((e as Error).message ?? "Couldn't send that message");
          return;
        }
      }
      await dispatchQueued(item);
    },
    [dispatchQueued, stableScope, threadId, turnBusy]
  );

  const editQueued = useCallback(
    async (qid: string, text: string) => {
      const trimmed = text.trim();
      const hasFiles = Boolean(
        queuedRef.current.find((q) => q.id === qid)?.attachments?.length
      );
      if (!threadId || (!trimmed && !hasFiles)) return;
      setQueued((cur) =>
        cur.map((q) => (q.id === qid ? { ...q, text: trimmed } : q))
      );
      try {
        const { thread } = await AgentAPI.updateQueued(
          stableScope,
          threadId,
          qid,
          trimmed
        );
        setQueued(thread.queued_messages ?? []);
      } catch (e) {
        setError((e as Error).message ?? "Couldn't edit that message");
        void refreshThreads();
      }
    },
    [refreshThreads, stableScope, threadId]
  );

  const removeQueued = useCallback(
    async (qid: string) => {
      if (!threadId) return;
      setQueued((cur) => cur.filter((q) => q.id !== qid));
      try {
        const thread = await AgentAPI.removeQueued(stableScope, threadId, qid);
        setQueued(thread.queued_messages ?? []);
      } catch (e) {
        setError((e as Error).message ?? "Couldn't remove that message");
        void refreshThreads();
      }
    },
    [refreshThreads, stableScope, threadId]
  );

  const reorderQueued = useCallback(
    async (ids: string[]) => {
      if (!threadId) return;
      const byId = new Map(queuedRef.current.map((q) => [q.id, q]));
      const next = ids
        .map((i) => byId.get(i))
        .filter(Boolean) as QueuedMessage[];
      if (next.length !== queuedRef.current.length) return;
      setQueued(next);
      try {
        const thread = await AgentAPI.reorderQueued(stableScope, threadId, ids);
        setQueued(thread.queued_messages ?? []);
      } catch (e) {
        setError((e as Error).message ?? "Couldn't reorder the queue");
        void refreshThreads();
      }
    },
    [refreshThreads, stableScope, threadId]
  );

  /**
   * Stop the running turn: abort our stream for instant feedback and ask the
   * server to halt the graph at its next step (which is what actually stops
   * the work, and what stops a teammate's turn). The queue pauses.
   */
  const stopTurn = useCallback(async () => {
    setQueuePaused(true);
    // A first message whose thread is still being created: never send it.
    if (pendingFirstRef.current) {
      pendingFirstRef.current = null;
      setPendingFirst(null);
      pendingSend.current = null;
    }
    const s = chatRef.current.status;
    if (s === "submitted" || s === "streaming") void chatRef.current.stop();
    if (!threadId) return;
    try {
      await AgentAPI.stopThread(stableScope, threadId);
    } catch (e) {
      setError((e as Error).message ?? "Couldn't stop the agent");
    }
  }, [stableScope, threadId]);

  /**
   * Dispatch the queue head when it is ours and the thread is free. Runs off
   * the thread update that clears `active_turn` (or our own turn finishing),
   * which is how a message queued during a teammate's turn gets sent by the
   * person who wrote it. The worker runs it after a grace period otherwise.
   */
  const dispatchingRef = useRef<string | null>(null);
  const currentThread = threads.find((t) => t.id === threadId) ?? null;
  const threadWaiting =
    Boolean(
      currentThread?.pending_approval || currentThread?.pending_question
    ) || transcriptWaiting(chat.messages);
  useEffect(() => {
    const head = queued[0];
    const myUid = meRef.current?.uid;
    if (
      !head ||
      !threadId ||
      queuePaused ||
      restoring ||
      threadWaiting ||
      activeTurn !== null ||
      !myUid ||
      head.uid !== myUid ||
      head.id.startsWith("pending_") ||
      chat.status === "submitted" ||
      chat.status === "streaming" ||
      pendingFirst !== null ||
      dispatchingRef.current === head.id
    )
      return;
    dispatchingRef.current = head.id;
    // Already ran (or repeats what just ran): drop it rather than run it twice.
    const run = alreadyInTranscript(head, chatRef.current.messages)
      ? removeQueued(head.id)
      : dispatchQueued(head);
    void run.finally(() => {
      if (dispatchingRef.current === head.id) dispatchingRef.current = null;
    });
  }, [
    activeTurn,
    chat.status,
    dispatchQueued,
    pendingFirst,
    queued,
    queuePaused,
    removeQueued,
    restoring,
    threadId,
    threadWaiting
  ]);
  // The ref, not the state: this runs in the same commit as the restore effect,
  // whose `setRestoring(true)` is not visible here yet. A restore in flight
  // would otherwise overwrite the message with the server's transcript once it
  // landed. `restoring` stays a dependency so this re-runs when it settles.
  useEffect(() => {
    const pending = pendingSend.current;
    if (
      !pending ||
      pending.threadId !== threadId ||
      !transport ||
      restoringRef.current
    )
      return;
    pendingSend.current = null;
    setPendingFirst(null);
    responderRef.current = pending.metadata?.responder ?? null;
    // With an id (the optimistic first row) the stored message keeps it, so
    // the row is replaced in place rather than duplicated.
    const message =
      pending.id !== undefined || pending.files?.length
        ? {
            ...(pending.id !== undefined ? { id: pending.id } : {}),
            role: "user" as const,
            parts: messageParts(pending.text, pending.files),
            metadata: pending.metadata
          }
        : { text: pending.text, metadata: pending.metadata };
    // The context from when Enter was pressed: the host may have released its
    // selection as soon as the status went to `submitted`.
    const body = {
      ...(pending.selection !== undefined
        ? { selection: pending.selection }
        : {}),
      ...(pending.body ?? {})
    };
    void (Object.keys(body).length
      ? chatRef.current.sendMessage(message, { body })
      : chatRef.current.sendMessage(message));
  }, [threadId, transport, restoring]);

  /**
   * Live updates from the shared event stream (`useAgentThreadLive`). A thread
   * update refreshes the list and the lock; a message is merged by id, but
   * never while this client is streaming its own turn.
   */
  const applyThreadUpdate = useCallback(
    (thread: AgentThread) => {
      // Another agent's thread on the same scope (the automation builder beside
      // the practice manager) is not this instance's to show.
      if (!ownThreads([thread]).length) return;
      setThreads((cur) => {
        const rest = cur.filter((t) => t.id !== thread.id);
        return [thread, ...rest].sort((a, b) =>
          (b.updated_at ?? "").localeCompare(a.updated_at ?? "")
        );
      });
      if (thread.id === threadIdRef.current) {
        applyTurnState(thread);
        // A turn started elsewhere (a teammate, the worker pool, this
        // person in another tab): watch it live rather than wait for the
        // finished message.
        attachToTurn(thread);
      } else {
        noteRunningTurn(thread);
      }
    },
    [applyTurnState, attachToTurn, noteRunningTurn, ownThreads]
  );
  const removeThread = useCallback(
    (id: string) => {
      setThreads((cur) => {
        const rest = cur.filter((t) => t.id !== id);
        if (threadIdRef.current === id) setThreadId(rest[0]?.id ?? null);
        return rest;
      });
    },
    [setThreadId]
  );
  /**
   * Re-fetch the active transcript from the server (after a stream gap, or
   * for teammates' messages that arrived while this client was streaming).
   * Skipped while a turn is in flight so it cannot clobber the live stream.
   */
  const reloadMessages = useCallback(async () => {
    const id = threadIdRef.current;
    if (!id) return false;
    const { current } = chatRef;
    if (current.status !== "ready" && current.status !== "error") return false;
    const { thread, messages } = await AgentAPI.getThread(stableScope, id);
    if (threadIdRef.current !== id) return false;
    const after = chatRef.current;
    if (after.status !== "ready" && after.status !== "error") return false;
    after.setMessages(messages as AgentUIMessage[]);
    applyTurnState(thread);
    return true;
  }, [applyTurnState, stableScope]);
  const applyMessageUpsert = useCallback(
    (forThreadId: string, message: AgentUIMessage) => {
      if (forThreadId !== threadIdRef.current) return false;
      const { current } = chatRef;
      if (current.status !== "ready" && current.status !== "error")
        return false;
      const existing = current.messages;
      const index = existing.findIndex((m) => m.id === message.id);
      if (index >= 0) {
        const next = [...existing];
        next[index] = message;
        current.setMessages(next);
      } else {
        current.setMessages([...existing, message]);
      }
      return true;
    },
    []
  );
  /**
   * Change one message in place (a support card's recorded state). Safe
   * while streaming except for the message being streamed, which is left
   * alone (`false`).
   */
  const patchMessage = useCallback(
    (id: string, fn: (message: AgentUIMessage) => AgentUIMessage) => {
      const { current } = chatRef;
      const existing = current.messages;
      const index = existing.findIndex((m) => m.id === id);
      if (index < 0) return false;
      const streaming =
        current.status === "streaming" || current.status === "submitted";
      if (streaming && index === existing.length - 1) return false;
      const next = [...existing];
      next[index] = fn(existing[index]);
      current.setMessages(next);
      return true;
    },
    []
  );
  /**
   * Record what the person did with a support handoff card (opened the
   * Messenger, or Not Now) on the message that carries the call, so a reload
   * shows the card settled.
   */
  const setSupportCard = useCallback(
    (toolCallId: string, state: SupportCardState) => {
      const message = chatRef.current.messages.find((m) =>
        m.parts.some((p) => "toolCallId" in p && p.toolCallId === toolCallId)
      );
      if (!message) return;
      patchMessage(message.id, (cur) => ({
        ...cur,
        metadata: {
          ...(cur.metadata ?? {}),
          support_cards: {
            ...(cur.metadata?.support_cards ?? {}),
            [toolCallId]: { state }
          }
        }
      }));
      if (!threadId) return;
      // Best-effort: the card has already moved on here.
      AgentAPI.setSupportCard(
        stableScope,
        threadId,
        message.id,
        toolCallId,
        state
      ).catch(() => undefined);
    },
    [patchMessage, stableScope, threadId]
  );
  /** Drop a message from the transcript (deleted here or by a teammate). */
  const removeMessage = useCallback((id: string) => {
    const { current } = chatRef;
    const existing = current.messages;
    if (!existing.some((m) => m.id === id)) return false;
    const streaming =
      current.status === "streaming" || current.status === "submitted";
    if (streaming && existing[existing.length - 1]?.id === id) return false;
    current.setMessages(existing.filter((m) => m.id !== id));
    return true;
  }, []);

  /**
   * The attorney answered an `ask_user` question. Writing the answers as the
   * tool part's output triggers `sendAutomaticallyWhen`, which resumes the
   * paused graph with them.
   */
  const answerQuestions = useCallback(
    (toolCallId: string, answers: AskUserAnswers) =>
      chatRef.current.addToolOutput({
        tool: "ask_user",
        toolCallId,
        output: { answers }
      }),
    []
  );

  /**
   * Answer an approval card. Beyond approve / decline with a reason, the card
   * may approve with edited arguments or allow the tool for the rest of this
   * thread; those ride on the part's `approval` (the SDK keeps extra keys),
   * which the server reads with the decision.
   */
  const respondToApproval = useCallback(async (decision: ApprovalDecision) => {
    const { id, approved, reason, edited_args, allow_for_thread } = decision;
    const extras: Record<string, unknown> = {};
    if (approved && edited_args) extras.edited_args = edited_args;
    if (approved && allow_for_thread) extras.allow_for_thread = true;
    if (Object.keys(extras).length) {
      const current = chatRef.current.messages;
      const last = current[current.length - 1];
      if (last) {
        chatRef.current.setMessages([
          ...current.slice(0, -1),
          {
            ...last,
            parts: last.parts.map((p) =>
              "approval" in p &&
              p.approval &&
              (p.approval as { id?: string }).id === id
                ? { ...p, approval: { ...p.approval, ...extras } }
                : p
            ) as typeof last.parts
          }
        ]);
      }
    }
    const busy =
      chatRef.current.status === "streaming" ||
      chatRef.current.status === "submitted";
    await chatRef.current.addToolApprovalResponse({
      id,
      approved,
      reason: reason?.trim() || undefined
    });
    const { messages } = chatRef.current;
    // While a stream is open the SDK sends once it ends; with other cards of
    // the pause still open it sends once they are decided.
    if (
      busy ||
      transcriptWaiting(messages) ||
      sendAutomaticallyWhen({ messages })
    )
      return;
    // The SDK marked the card decided but will send nothing: put it back so it
    // does not read "Approved" with nothing on its way.
    const last = messages[messages.length - 1];
    if (!last) return;
    chatRef.current.setMessages([
      ...messages.slice(0, -1),
      {
        ...last,
        parts: last.parts.map((p) =>
          "approval" in p &&
          p.approval &&
          (p.approval as { id?: string }).id === id &&
          p.state === "approval-responded"
            ? { ...p, state: "approval-requested", approval: { id } }
            : p
        ) as typeof last.parts
      }
    ]);
    toast.error("Your decision did not reach the agent. Try again.");
  }, []);

  /**
   * The connect card saw the apps connected (or the attorney skipped). Writing
   * the outcome as the `connect_apps` part's output resumes the paused graph
   * the same way an answered question does; the server re-checks the statuses
   * itself before telling the model.
   */
  const resolveConnect = useCallback(
    (toolCallId: string, output: ConnectAppsOutput) =>
      chatRef.current.addToolOutput({
        tool: "connect_apps",
        toolCallId,
        output
      }),
    []
  );

  /** The Meet Your Agents card was saved or skipped; resumes like `resolveConnect`. */
  const resolveSuggestAgents = useCallback(
    (toolCallId: string, output: SuggestedAgentsOutput) =>
      chatRef.current.addToolOutput({
        tool: "show_suggested_agents",
        toolCallId,
        output
      }),
    []
  );

  /**
   * Build a plan: the server flips the plan to building and its thread to Agent
   * mode; the chat switches to that thread if needed and sends the build prompt
   * (tagged so the transcript shows a Build card instead of the prompt).
   */
  const buildPlan = useCallback(
    async (id: string) => {
      const { thread, plan, prompt } = await AgentAPI.buildPlan(
        stableScope,
        id
      );
      modeRef.current = "agent";
      setModeState("agent");
      planIdRef.current = plan.id;
      setPlanId(plan.id);
      setThreads((cur) => {
        const rest = cur.filter((t) => t.id !== thread.id);
        return [thread, ...rest];
      });
      // The plan pane shows "Building" right away rather than on the next hint.
      adaptersRef.current?.onInvalidate?.(["plans"]);
      const build = { plan_id: plan.id, title: plan.title, todos: plan.todos };
      const metadata = stamp(prompt, "agent", { build });
      if (thread.id !== threadId) {
        pendingSend.current = { threadId: thread.id, text: prompt, metadata };
        setThreadId(thread.id);
        return plan;
      }
      if (turnBusy() || queuedRef.current.length) {
        // A direct send would lose the turn lock; the queue keeps it a Build
        // and runs it as its own turn.
        await enqueue(prompt, [], { build });
        return plan;
      }
      responderRef.current = null;
      await chatRef.current.sendMessage({ text: prompt, metadata });
      return plan;
    },
    [enqueue, setThreadId, stableScope, stamp, threadId, turnBusy]
  );

  /**
   * A fixed prompt as an Agent-mode turn on `target`, switching to it first
   * when another thread is open. `skillId` rides this turn's body only (`""`
   * sends none). Returns the message id, or null while the thread is busy:
   * the caller waits for it rather than queueing, since a queued message
   * would lose the skill. Does not wait for the turn.
   */
  const sendOnThread = useCallback(
    (
      target: string,
      text: string,
      options: { skillId?: string | null; metadata?: AgentMessageMetadata } = {}
    ): string | null => {
      const id = `user_${crypto.randomUUID().replace(/-/g, "")}`;
      const metadata = stamp(text, "agent", options.metadata);
      const body = { mode: "agent", skill_id: options.skillId ?? "" };
      if (target !== threadId) {
        pendingSend.current = { threadId: target, id, text, metadata, body };
        setThreadId(target);
        return id;
      }
      if (turnBusy() || queuedRef.current.length) return null;
      setQueuePaused(false);
      responderRef.current = null;
      void chatRef.current.sendMessage(
        { id, role: "user", parts: messageParts(text), metadata },
        { body }
      );
      return id;
    },
    [setThreadId, stamp, threadId, turnBusy]
  );

  return {
    scope: stableScope,
    threads,
    threadsLoading,
    threadId,
    setThreadId,
    fixed,
    newThread,
    startNewThread,
    deleteThread,
    renameThread,
    setPinned,
    setArchived,
    refreshThreads,
    // A first message shows (with its turn "submitted") from the moment Enter
    // is pressed, while its thread is still being created.
    messages:
      pendingFirst && chat.messages.length === 0
        ? [pendingFirst]
        : chat.messages,
    status: pendingFirst ? "submitted" : chat.status,
    /** Live subagent steps by `task` call id (from `data-step`). */
    turnSteps,
    /** Aborts only our stream; `stopTurn` is what halts the work. */
    stop: chat.stop,
    stopTurn,
    restoring,
    error,
    activeTurn,
    /** Someone asked the running turn to stop; it halts at its next step. */
    stopRequested,
    /** "Send now" messages the running turn has not picked up yet. */
    steerInbox,
    sendNow,
    /** Messages waiting for the running turn (shared by everyone in the thread). */
    queue: {
      items: queued,
      paused: queuePaused,
      enqueue,
      edit: editQueued,
      remove: removeQueued,
      reorder: reorderQueued,
      sendNow: sendQueuedNow,
      resume: () => setQueuePaused(false)
    },
    applyThreadUpdate,
    removeThread,
    applyMessageUpsert,
    patchMessage,
    setSupportCard,
    removeMessage,
    reloadMessages,
    clearError: () => {
      setError(null);
      chat.clearError();
    },
    send,
    addToolApprovalResponse: chat.addToolApprovalResponse,
    respondToApproval,
    regenerate: chat.regenerate,
    mode,
    setMode,
    skillId,
    setSkill,
    /** The chip's skill stays for every later turn. */
    skillPinned,
    setSkillPinned,
    planId,
    /** The picked catalog model; `null` is Auto. */
    model: picks.model,
    thinking: picks.thinking,
    optimizeFor: picks.optimizeFor,
    setModel,
    setThinking,
    setOptimizeFor,
    answerQuestions,
    resolveConnect,
    resolveSuggestAgents,
    buildPlan,
    sendOnThread
  };
};

export type AgentChat = ReturnType<typeof useAgentChat>;
